import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useSearchParams, useParams } from "react-router-dom";
import { Eye, EyeOff, Info, AlertTriangle, Sparkles, Users, Flame, Building2, Video } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import api, { formatApiError } from "@/lib/api";
import { REGIONES, ESPECIALIDADES_ABG, ESPECIALIDADES_MED, CORTES_APELACIONES, CORTES_MAP, comunasForRegion } from "@/lib/constants";
import { formatRut, cleanRut, isValidRut } from "@/lib/rut";

const CLP = (n) => new Intl.NumberFormat("es-CL", { style: "currency", currency: "CLP", maximumFractionDigits: 0 }).format(n || 0);

export default function Register() {
  const { register } = useAuth();
  const { config } = useConfig();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const routeParams = useParams();
  const [showPwd, setShowPwd] = useState(false);
  // Rol forzado por URL: query `?tipo=...` OR ruta `/registro/:tipo`
  // Si viene con tipo → se oculta el selector de rol para evitar mala praxis.
  const tipoParam = ((routeParams.tipo || params.get("tipo") || "").toLowerCase());
  const TIPO_TO_ROLE = { particular: "requester", solicitante: "requester", abogado: "lawyer", mediador: "mediator" };
  const forcedRole = TIPO_TO_ROLE[tipoParam] || null;
  const isForcedRequester = forcedRole === "requester";
  // requester sólo se permite si el feature flag está activo
  const initialRole = forcedRole
    ? (isForcedRequester ? (config.new_mediation_enabled ? "requester" : "lawyer") : forcedRole)
    : "lawyer";
  const [form, setForm] = useState({
    role: initialRole, name: "", email: "", password: "", rut: "", phone: "",
    phone_local: "", // 8 dígitos que van después del prefijo +56 9
    region: "", comuna: "", domicilio_profesional: "", specialties: [], registry_number: "", bio: "",
    certificate_filename: "", certificate_mime: "", certificate_b64: "",
    // === Campos de Mediador Familiar Privado ===
    cortes_apelaciones: [], comunas_atendidas: [], office_address: "",
    attends_presencial: false, attends_remote: false,
    precio_mediador_clp: 0, precio_acuerdo_clp: 0,
    // === Aceptación legal obligatoria ===
    terms_accepted: false,
    // === Programa de referidos Pionero ===
    referral_code: "",
    // === Foto de perfil (opcional) — se guarda como base64 al backend ===
    profile_photo_b64: "", profile_photo_mime: "", profile_photo_name: "",
    // === Datos bancarios (opcional al registro, requerido para recibir pagos) ===
    bank_name: "", bank_account_type: "", bank_account_number: "", bank_holder_rut: "", bank_email: "",
  });

  // Prefill from /inscripcion o /invitar?ref=CODIGO query params
  useEffect(() => {
    const updates = {};
    ["name", "email", "phone"].forEach((k) => {
      const v = params.get(k);
      if (v) updates[k] = v;
    });
    const ref = params.get("ref");
    if (ref) updates.referral_code = ref.toUpperCase().trim();
    // Si viene un teléfono, extraer los últimos 8 dígitos para phone_local
    if (updates.phone) {
      const digits = String(updates.phone).replace(/\D/g, "");
      updates.phone_local = digits.startsWith("569") ? digits.slice(3, 11) : digits.slice(-8);
    }
    if (Object.keys(updates).length) setForm((f) => ({ ...f, ...updates }));
  }, [params]);

  // Sync role forzado desde ?tipo= una vez que el config termine de cargar (flag puede llegar tarde)
  useEffect(() => {
    if (!forcedRole) return;
    const target = isForcedRequester ? (config.new_mediation_enabled ? "requester" : "lawyer") : forcedRole;
    setForm((f) => f.role === target ? f : { ...f, role: target });
  }, [forcedRole, isForcedRequester, config.new_mediation_enabled]);
  const [error, setError] = useState("");
  const [rutFieldError, setRutFieldError] = useState("");
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSpec = (s) =>
    setForm((f) => ({ ...f, specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s] }));

  // Toggle multi-select para Cortes de Apelaciones (con limpieza automática de comunas fuera de scope)
  const toggleCorte = (code) => {
    setForm((f) => {
      const has = f.cortes_apelaciones.includes(code);
      const nextCortes = has ? f.cortes_apelaciones.filter((c) => c !== code) : [...f.cortes_apelaciones, code];
      // Comunas disponibles con las nuevas cortes seleccionadas
      const allComunas = new Set(nextCortes.flatMap((cc) => CORTES_MAP[cc]?.comunas || []));
      const nextComunas = f.comunas_atendidas.filter((c) => allComunas.has(c));
      return { ...f, cortes_apelaciones: nextCortes, comunas_atendidas: nextComunas };
    });
  };
  const toggleComuna = (c) => {
    setForm((f) => ({ ...f, comunas_atendidas: f.comunas_atendidas.includes(c) ? f.comunas_atendidas.filter((x) => x !== c) : [...f.comunas_atendidas, c] }));
  };

  // Comunas visibles = unión de las comunas de todas las cortes seleccionadas
  const availableComunas = useMemo(() => {
    const s = new Set(form.cortes_apelaciones.flatMap((cc) => CORTES_MAP[cc]?.comunas || []));
    return Array.from(s).sort((a, b) => a.localeCompare(b, "es"));
  }, [form.cortes_apelaciones]);

  // Cálculo transparente del tarifario mediador (lee valores desde el motor de
  // Configuración de Negocio dinámica, mismos parámetros que el backend).
  const initialFee = Number(config?.business_config?.cargo_fijo_mediacion ?? config?.colega_link_initial_fee_clp ?? 13200);
  const agreementRate = Number(config?.business_config?.porcentaje_comision_acuerdo ?? config?.colega_link_agreement_rate ?? 0.08);
  const totalCap = Number(config?.business_config?.tope_total_solicitante_clp ?? config?.family_mediation_total_cap_clp ?? 130602);
  const pricing = useMemo(() => {
    const base = Math.max(0, parseInt(form.precio_mediador_clp || 0, 10));
    const adicional = Math.max(0, parseInt(form.precio_acuerdo_clp || 0, 10));
    const totalFrustrada = base + initialFee;
    const comision = Math.ceil(adicional * agreementRate);
    const totalAcuerdo = totalFrustrada + adicional + comision;
    const maxAdicional = Math.max(0, Math.floor((totalCap - base - initialFee) / (1 + agreementRate)));
    return { base, adicional, totalFrustrada, comision, totalAcuerdo, maxAdicional, overCap: totalAcuerdo > totalCap };
  }, [form.precio_mediador_clp, form.precio_acuerdo_clp, initialFee, agreementRate, totalCap]);

  // === FOMO stats: contadores en vivo según el rol y la selección ===
  const [fomoMed, setFomoMed] = useState(null);   // { counts_by_corte, total_mediators }
  const [fomoLaw, setFomoLaw] = useState(null);   // { region_lawyers_count, founder_remaining, total_lawyers }

  // Mediador: refrescar contadores por corte al cambiar selección
  useEffect(() => {
    if (form.role !== "mediator" || form.cortes_apelaciones.length === 0) { setFomoMed(null); return; }
    const controller = new AbortController();
    const t = setTimeout(() => {
      api.get(`/fomo/register-stats?role=mediator&cortes=${form.cortes_apelaciones.join(",")}`, { signal: controller.signal })
        .then(({ data }) => setFomoMed(data))
        .catch(() => {});
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [form.role, form.cortes_apelaciones]);

  // Abogado: refrescar contadores al cambiar región
  useEffect(() => {
    if (form.role !== "lawyer") { setFomoLaw(null); return; }
    const controller = new AbortController();
    const q = form.region ? `?role=lawyer&region=${encodeURIComponent(form.region)}` : "?role=lawyer";
    const t = setTimeout(() => {
      api.get(`/fomo/register-stats${q}`, { signal: controller.signal })
        .then(({ data }) => setFomoLaw(data))
        .catch(() => {});
    }, 250);
    return () => { clearTimeout(t); controller.abort(); };
  }, [form.role, form.region]);

  const onCertificateChange = async (file) => {
    if (!file) {
      set("certificate_filename", ""); set("certificate_mime", ""); set("certificate_b64", "");
      return;
    }
    if (file.size > 5 * 1024 * 1024) { setError("El certificado supera 5MB"); return; }
    const data_b64 = await new Promise((resolve, reject) => {
      const fr = new FileReader();
      fr.onload = () => resolve(String(fr.result).split(",")[1]);
      fr.onerror = reject;
      fr.readAsDataURL(file);
    });
    setForm((f) => ({ ...f, certificate_filename: file.name, certificate_mime: file.type || "application/pdf", certificate_b64: data_b64 }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      // Aceptación legal obligatoria para TODOS los roles
      if (!form.terms_accepted) {
        setError("Debes aceptar los Términos de Servicio y la cláusula de exención de responsabilidad para crear tu cuenta.");
        setLoading(false);
        return;
      }
      // Teléfono: componer +56 9 + 8 dígitos (obligatorio para todos)
      const digits = String(form.phone_local || "").replace(/\D/g, "").slice(0, 8);
      if (digits.length !== 8) {
        setError("El teléfono debe tener 8 dígitos después del prefijo +56 9.");
        setLoading(false);
        return;
      }
      const composedPhone = `+56 9 ${digits}`;
      // Normalizar RUT al formato canónico DDDDDDDD-V antes de enviar.
      // RUT es OBLIGATORIO para todos los roles (solicitante, abogado, mediador).
      if (!form.rut || !form.rut.trim()) {
        setError("El RUT es obligatorio.");
        setLoading(false);
        return;
      }
      if (!isValidRut(form.rut)) {
        setError("RUT inválido. Verifica el número y el dígito verificador.");
        setLoading(false);
        return;
      }
      // Solicitante: dirección particular es obligatoria
      if (form.role === "requester") {
        if (!form.domicilio_profesional.trim()) {
          setError("La Dirección Particular es obligatoria.");
          setLoading(false);
          return;
        }
      }
      // Validación tarifario mediador (si ingresó precio_mediador_clp)
      if (form.role === "mediator" && pricing.base > 0 && pricing.adicional > pricing.maxAdicional) {
        setError(`El precio adicional con acuerdo (${CLP(pricing.adicional)}) supera el máximo permitido de ${CLP(pricing.maxAdicional)} para no exceder el tope legal de ${CLP(totalCap)}.`);
        setLoading(false);
        return;
      }
      // Validación modalidad de atención (mediador)
      if (form.role === "mediator" && !form.attends_presencial && !form.attends_remote) {
        setError("Debes indicar al menos una modalidad de atención: Presencial o Telemática.");
        setLoading(false);
        return;
      }
      if (form.role === "mediator" && form.attends_presencial && !form.office_address.trim()) {
        setError("Si atiendes en modalidad Presencial, debes ingresar la dirección de tu oficina.");
        setLoading(false);
        return;
      }
      const normalizedRut = form.rut ? cleanRut(form.rut) : "";
      // For Persona solicitante we send only the simple fields the backend expects
      const payload = form.role === "requester"
        ? {
            role: "requester",
            name: form.name,
            email: form.email,
            password: form.password,
            phone: composedPhone,
            comuna: form.comuna,
            region: form.region,
            rut: normalizedRut,
            domicilio_profesional: form.domicilio_profesional.trim(),
          }
        : { ...form, rut: normalizedRut, phone: composedPhone };
      // Nunca enviamos phone_local ni terms_accepted al backend
      if (payload.phone_local !== undefined) delete payload.phone_local;
      if (payload.terms_accepted !== undefined) delete payload.terms_accepted;
      await register(payload);
      const next = params.get("next");
      nav(next && next.startsWith("/") ? next : "/app");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const isRequester = form.role === "requester";
  const ESPS = form.role === "mediator" ? ESPECIALIDADES_MED : ESPECIALIDADES_ABG;

  return (
    <div className="container-app py-16 grid lg:grid-cols-12 gap-12 items-start">
      <div className="lg:col-span-4">
        <div className="label-eyebrow">Crear cuenta</div>
        <h1 className="serif text-5xl mt-3 leading-tight">Únete a la comunidad de profesionales verificados.</h1>
        <p className="mt-6 text-muted-foreground">Tu cuenta queda en revisión hasta que un administrador valide tus credenciales. Mientras, puedes navegar la plataforma.</p>
      </div>
      <form onSubmit={submit} className="lg:col-span-8 panel p-10" data-testid="register-form">
        {forcedRole ? (
          <div className="mb-6 flex items-center gap-3 p-4 rounded-md border border-cyan-400/30 bg-cyan-400/5" data-testid="forced-role-banner">
            <div className="w-9 h-9 rounded-full bg-cyan-400/15 border border-cyan-400/40 flex items-center justify-center text-cyan-300 text-lg font-bold">
              {form.role === "requester" ? "P" : form.role === "mediator" ? "M" : "A"}
            </div>
            <div>
              <div className="label-eyebrow text-cyan-300">Registro exclusivo</div>
              <div className="text-sm mt-0.5">
                {form.role === "requester" && "Cuenta de Persona Solicitante · para pedir mediación familiar privada"}
                {form.role === "mediator" && "Cuenta de Mediador/a Familiar · para operar en el directorio"}
                {form.role === "lawyer" && "Cuenta de Abogado/a · para publicar y tomar delegaciones"}
              </div>
            </div>
          </div>
        ) : (
          <div className={`grid gap-2 mb-6 ${config.new_mediation_enabled ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
            <button type="button" onClick={() => set("role", "lawyer")} className={`btn-ghost py-3 ${form.role === "lawyer" ? "border-foreground/60 bg-secondary" : ""}`} data-testid="role-lawyer">Soy Abogado/a</button>
            <button type="button" onClick={() => set("role", "mediator")} className={`btn-ghost py-3 ${form.role === "mediator" ? "border-foreground/60 bg-secondary" : ""}`} data-testid="role-mediator">Soy Mediador/a</button>
            {config.new_mediation_enabled && (
              <button type="button" onClick={() => set("role", "requester")} className={`btn-ghost py-3 ${form.role === "requester" ? "border-foreground/60 bg-secondary" : ""}`} data-testid="role-requester">Persona solicitante</button>
            )}
          </div>
        )}

        {form.role === "lawyer" && (
          <div className="mb-6 rounded-md border border-amber-400/25 bg-amber-400/5 p-3 text-sm flex items-center justify-between gap-3" data-testid="reg-lawyer-plans-cta">
            <div>
              <div className="font-medium text-amber-200">¿Quieres ver los planes disponibles?</div>
              <div className="text-xs text-muted-foreground mt-0.5">Reto Pionero ($15.000 · primeros 50), Pionero Fundador, Premium y Free.</div>
            </div>
            <Link to="/planes" className="btn-ghost text-xs border-amber-400/40 hover:bg-amber-400/10 whitespace-nowrap" data-testid="reg-lawyer-view-plans">Ver planes</Link>
          </div>
        )}

        {isRequester && (
          <div className="mb-6 p-4 rounded-md border border-cyan-400/25 bg-cyan-400/5 text-sm text-cyan-100/90" data-testid="requester-intro">
            Cuenta simple para solicitar una <strong>mediación familiar privada</strong>. No requiere certificado profesional.
          </div>
        )}

        {/* FOMO Abogado · cupos Pionero + colegas por región */}
        {form.role === "lawyer" && fomoLaw && (
          <div className="mb-6 grid sm:grid-cols-2 gap-3" data-testid="reg-fomo-lawyer">
            {fomoLaw.founder_remaining > 0 ? (
              <div className="p-4 rounded-md border border-amber-400/30 bg-amber-400/5" data-testid="reg-fomo-founder">
                <div className="flex items-center gap-2">
                  <Flame size={14} className="text-amber-300"/>
                  <span className="label-eyebrow text-amber-300">Cupos Reto Pionero</span>
                </div>
                <div className="serif text-3xl mt-2 text-amber-200">
                  {fomoLaw.founder_remaining}<span className="text-sm text-muted-foreground ml-1">/ {fomoLaw.founder_cap} restantes</span>
                </div>
                <div className="text-[11px] text-muted-foreground mt-1">
                  Los primeros 50 abogados que paguen $15.000 CLP de reserva acceden a Colega IA (30 consultas/mes), Biblioteca Jurídica y 0% de comisión vitalicia si completan el reto de 15 referidos.
                </div>
              </div>
            ) : (
              <div className="p-4 rounded-md border border-white/10 bg-white/[0.02]" data-testid="reg-fomo-founder-closed">
                <div className="label-eyebrow text-muted-foreground">Cupos Pionero</div>
                <div className="serif text-3xl mt-2 text-muted-foreground">Cerrados</div>
                <div className="text-[11px] text-muted-foreground mt-1">Los 50 cupos ya fueron ocupados. Sigues con acceso al plan Free o Premium.</div>
              </div>
            )}
            <div className="p-4 rounded-md border border-cyan-400/25 bg-cyan-400/5" data-testid="reg-fomo-region">
              <div className="flex items-center gap-2">
                <Users size={14} className="text-cyan-300"/>
                <span className="label-eyebrow text-cyan-300">Colegas en Colega Link</span>
              </div>
              {form.region ? (
                <>
                  <div className="serif text-3xl mt-2 text-cyan-200">
                    {fomoLaw.region_lawyers_count}<span className="text-sm text-muted-foreground ml-1">en {form.region}</span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-1">
                    {fomoLaw.region_lawyers_count === 0
                      ? <>¡Serás el <strong className="text-emerald-300">primer abogado</strong> de tu región en la plataforma!</>
                      : <>Ya hay {fomoLaw.region_lawyers_count} colega{fomoLaw.region_lawyers_count === 1 ? "" : "s"} en tu región. Total nacional: <strong className="text-foreground">{fomoLaw.total_lawyers}</strong>.</>}
                  </div>
                </>
              ) : (
                <>
                  <div className="serif text-3xl mt-2 text-cyan-200">{fomoLaw.total_lawyers}</div>
                  <div className="text-[11px] text-muted-foreground mt-1">Abogados verificados a nivel nacional. Elige tu región para ver el ranking local.</div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Banner de referido Pionero */}
        {form.referral_code && form.role === "lawyer" && (
          <div className="mt-4 p-4 rounded-md border border-amber-400/30 bg-gradient-to-br from-amber-950/25 to-transparent" data-testid="reg-referral-banner">
            <div className="flex items-start gap-3">
              <span className="text-2xl">🎟️</span>
              <div className="flex-1">
                <div className="text-sm text-amber-200 font-semibold">
                  Te invita un Pionero de ColegaLink
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Código: <span className="font-mono text-amber-300">{form.referral_code}</span> — al completar tu registro y verificar tu cuenta, contarás como referido validado de tu colega Pionero.
                </div>
              </div>
              <button
                type="button"
                onClick={() => set("referral_code", "")}
                className="text-xs text-muted-foreground hover:text-foreground"
                data-testid="reg-referral-remove"
              >
                Quitar
              </button>
            </div>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Nombre completo *"><input className="field" required value={form.name} onChange={(e)=>set("name", e.target.value)} data-testid="reg-name"/></Field>
          <Field label="RUT *" hint={rutFieldError}>
            <input className={`field ${rutFieldError ? "border-red-400/60 focus:border-red-400" : ""}`} placeholder="12345678-9" required
              value={form.rut}
              onChange={(e)=>{
                const formatted = formatRut(e.target.value);
                set("rut", formatted);
                // Limpiar error de RUT en cuanto queda vacío o vuelve a ser válido
                if (!formatted || isValidRut(formatted)) {
                  setRutFieldError("");
                  if (error && error.startsWith("RUT inválido")) setError("");
                }
              }}
              onBlur={(e)=>{
                const v = e.target.value;
                if (v && !isValidRut(v)) setRutFieldError("RUT inválido. Verifica el número y el dígito verificador.");
                else setRutFieldError("");
              }}
              data-testid="reg-rut"/>
          </Field>
          <Field label="Correo *"><input className="field" type="email" required value={form.email} onChange={(e)=>set("email", e.target.value)} data-testid="reg-email"/></Field>
          <Field label="Contraseña *">
            <div className="relative">
              <input className="field pr-10 w-full" type={showPwd ? "text" : "password"} required minLength={6} value={form.password} onChange={(e)=>set("password", e.target.value)} data-testid="reg-password" autoComplete="new-password"/>
              <button type="button" onClick={()=>setShowPwd(!showPwd)} tabIndex={-1} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-foreground hover:text-foreground" data-testid="reg-password-toggle" aria-label={showPwd ? "Ocultar contraseña" : "Mostrar contraseña"}>
                {showPwd ? <EyeOff size={15}/> : <Eye size={15}/>}
              </button>
            </div>
          </Field>
          <Field label="Teléfono *">
            <div className="flex">
              <span
                className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-white/10 bg-white/5 text-sm text-muted-foreground select-none"
                data-testid="reg-phone-prefix"
              >
                +56 9
              </span>
              <input
                className="field rounded-l-none flex-1"
                required
                inputMode="numeric"
                pattern="[0-9]{8}"
                maxLength={8}
                value={form.phone_local}
                onChange={(e) => set("phone_local", e.target.value.replace(/\D/g, "").slice(0, 8))}
                placeholder="12345678"
                data-testid="reg-phone"
              />
            </div>
          </Field>
          {form.role === "mediator" && (
            <Field label="N° Registro Min. Justicia (opcional)"><input className="field" value={form.registry_number} onChange={(e)=>set("registry_number", e.target.value)} data-testid="reg-registry" placeholder="Ej: ABG-12345"/></Field>
          )}
          <Field label="Región *">
            <select className="field" required value={form.region} onChange={(e)=>{
              const r = e.target.value;
              const nuevas = comunasForRegion(r);
              setForm((f) => ({ ...f, region: r, comuna: nuevas.includes(f.comuna) ? f.comuna : "" }));
            }} data-testid="reg-region">
              <option value="">Selecciona…</option>
              {REGIONES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </Field>
          <Field label="Comuna *">
            <select className="field" required value={form.comuna} onChange={(e)=>set("comuna", e.target.value)} data-testid="reg-comuna" disabled={!form.region}>
              <option value="">{form.region ? "Selecciona…" : "Primero elige región"}</option>
              {comunasForRegion(form.region).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
        </div>

        <Field className="mt-4" label={
          isRequester
            ? "Dirección Particular *"
            : form.role === "lawyer"
              ? "Domicilio profesional * (para la Minuta Delega Poder)"
              : "Domicilio profesional *"
        }>
          <input className="field" required
            value={form.domicilio_profesional}
            onChange={(e)=>set("domicilio_profesional", e.target.value)}
            placeholder={isRequester ? "Calle 123, Depto 45, Comuna" : "Ej: Av. Apoquindo 4501, Of. 1205, Las Condes"}
            data-testid="reg-domicilio"/>
        </Field>

        {!isRequester && (
          <div className="mt-6">
            <div className="label-eyebrow mb-2">Especialidades</div>
            <div className="flex flex-wrap gap-2">
              {ESPS.map((s) => (
                <button type="button" key={s} onClick={()=>toggleSpec(s)}
                  className={`pill ${form.specialties.includes(s) ? "bg-foreground text-background" : ""}`}
                  data-testid={`spec-${s}`}>{s}</button>
              ))}
            </div>
          </div>
        )}

        {!isRequester && (
          <Field className="mt-6" label="Bio breve">
            <textarea className="field" rows={3} value={form.bio} onChange={(e)=>set("bio", e.target.value)} data-testid="reg-bio"/>
          </Field>
        )}

        {form.role === "mediator" && (
          <div className="mt-8 panel p-6 space-y-6" data-testid="reg-mediator-family-section">
            <div>
              <div className="label-eyebrow text-cyan-300">Mediación Familiar Privada</div>
              <h3 className="serif text-2xl mt-1">Datos del directorio y tarifario</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Esta información aparecerá en tu <strong>ficha pública</strong> del directorio y en el flujo de solicitud de mediación.
              </p>
            </div>

            {/* Cortes de Apelaciones */}
            <div>
              <div className="label-eyebrow">Cortes de Apelaciones donde tienes competencia *</div>
              <p className="text-xs text-muted-foreground mt-1 mb-3">Selecciona una o más. Puedes tener competencia en distintos territorios jurisdiccionales.</p>
              <div className="flex flex-wrap gap-2" data-testid="reg-cortes-container">
                {CORTES_APELACIONES.map((c) => (
                  <button type="button" key={c.code} onClick={() => toggleCorte(c.code)}
                    className={`pill ${form.cortes_apelaciones.includes(c.code) ? "bg-cyan-400 text-black border-cyan-400" : ""}`}
                    data-testid={`reg-corte-${c.code}`}>
                    {c.name}
                  </button>
                ))}
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">
                {form.cortes_apelaciones.length === 0 ? "Ninguna seleccionada" : `${form.cortes_apelaciones.length} corte${form.cortes_apelaciones.length === 1 ? "" : "s"} seleccionada${form.cortes_apelaciones.length === 1 ? "" : "s"}`}
              </div>

              {/* FOMO Mediador · contadores por corte */}
              {fomoMed && form.cortes_apelaciones.length > 0 && (
                <div className="mt-4 panel p-4 border-amber-400/30 bg-amber-400/5 space-y-2" data-testid="reg-fomo-mediator">
                  <div className="flex items-center gap-2">
                    <Sparkles size={14} className="text-amber-300"/>
                    <span className="label-eyebrow text-amber-300">Tu posición en el directorio</span>
                  </div>
                  <ul className="space-y-1.5 text-sm">
                    {form.cortes_apelaciones.map((code) => {
                      const cortesInfo = CORTES_APELACIONES.find((cc) => cc.code === code);
                      const n = fomoMed.counts_by_corte?.[code] ?? 0;
                      const position = n + 1;
                      const isFirst = n === 0;
                      return (
                        <li key={code} className="flex items-start gap-2" data-testid={`reg-fomo-corte-${code}`}>
                          {isFirst
                            ? <Flame size={13} className="text-emerald-300 mt-0.5 shrink-0"/>
                            : <span className="text-amber-300 text-xs mt-0.5">▸</span>}
                          <span>
                            {isFirst ? (
                              <><strong className="text-emerald-300">¡Serás el 1er mediador</strong> de la Corte de <strong>{cortesInfo?.name}</strong>!</>
                            ) : (
                              <>Serás el <strong className="text-amber-200">{ordinal(position)} mediador</strong> de la Corte de <strong>{cortesInfo?.name}</strong> <span className="text-muted-foreground">({n} ya registrado{n === 1 ? "" : "s"})</span></>
                            )}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  {fomoMed.total_mediators > 0 && (
                    <div className="text-[11px] text-muted-foreground pt-1 border-t border-white/5 mt-2">
                      Total nacional en Colega Link: <strong className="text-foreground">{fomoMed.total_mediators} mediador{fomoMed.total_mediators === 1 ? "" : "es"}</strong> registrado{fomoMed.total_mediators === 1 ? "" : "s"}.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Comunas donde opera */}
            <div>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="label-eyebrow">Comunas donde operas * {availableComunas.length > 0 && <span className="text-muted-foreground">({form.comunas_atendidas.length} de {availableComunas.length})</span>}</div>
                {availableComunas.length > 0 && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, comunas_atendidas: [...availableComunas] }))}
                      className="text-[11px] px-2.5 py-1 rounded-md border border-cyan-400/30 text-cyan-200 hover:bg-cyan-400/10"
                      data-testid="reg-comunas-select-all"
                    >
                      Seleccionar todas
                    </button>
                    {form.comunas_atendidas.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, comunas_atendidas: [] }))}
                        className="text-[11px] px-2.5 py-1 rounded-md border border-white/15 text-muted-foreground hover:text-foreground"
                        data-testid="reg-comunas-clear-all"
                      >
                        Limpiar
                      </button>
                    )}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                {form.cortes_apelaciones.length === 0
                  ? "Primero selecciona al menos una Corte de Apelaciones para ver las comunas disponibles."
                  : "Se muestran las comunas de las cortes seleccionadas. Elige aquellas en las que efectivamente atiendes."}
              </p>
              {availableComunas.length > 0 && (
                <div className="flex flex-wrap gap-2 max-h-56 overflow-auto pr-2" data-testid="reg-comunas-container">
                  {availableComunas.map((c) => (
                    <button type="button" key={c} onClick={() => toggleComuna(c)}
                      className={`pill text-xs ${form.comunas_atendidas.includes(c) ? "bg-emerald-400 text-black border-emerald-400" : ""}`}
                      data-testid={`reg-comuna-${c}`}>
                      {c}
                    </button>
                  ))}
                </div>
              )}
              <div className="text-[11px] text-muted-foreground mt-2">
                {form.comunas_atendidas.length === 0 ? "Ninguna comuna seleccionada" : `${form.comunas_atendidas.length} comuna${form.comunas_atendidas.length === 1 ? "" : "s"} seleccionada${form.comunas_atendidas.length === 1 ? "" : "s"}`}
              </div>
            </div>

            {/* Modalidad de atención (obligatorio: al menos una) */}
            <div>
              <div className="label-eyebrow">Modalidad de atención *</div>
              <p className="text-xs text-muted-foreground mt-1 mb-3">
                Marca al menos una. Es obligatorio para publicar tu ficha en el Directorio y recibir solicitudes.
              </p>
              <div className="grid sm:grid-cols-2 gap-3" data-testid="reg-modality-group">
                <label
                  className={`panel p-4 cursor-pointer transition-colors ${form.attends_presencial ? "ring-1 ring-cyan-400 border-cyan-400/40" : "hover:border-cyan-400/40"}`}
                  data-testid="reg-modality-presencial-label"
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={form.attends_presencial}
                    onChange={(e) => set("attends_presencial", e.target.checked)}
                    data-testid="reg-attends-presencial"
                  />
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${form.attends_presencial ? "bg-cyan-400 border-cyan-400" : "border-white/30"}`}>
                      {form.attends_presencial && <span className="text-black text-xs font-bold">✓</span>}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Building2 size={14} className={form.attends_presencial ? "text-cyan-300" : "text-muted-foreground"}/> Atención Presencial
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">En oficina del mediador. Requiere ingresar dirección de oficina abajo.</div>
                    </div>
                  </div>
                </label>

                <label
                  className={`panel p-4 cursor-pointer transition-colors ${form.attends_remote ? "ring-1 ring-cyan-400 border-cyan-400/40" : "hover:border-cyan-400/40"}`}
                  data-testid="reg-modality-remote-label"
                >
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={form.attends_remote}
                    onChange={(e) => set("attends_remote", e.target.checked)}
                    data-testid="reg-attends-remote"
                  />
                  <div className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${form.attends_remote ? "bg-cyan-400 border-cyan-400" : "border-white/30"}`}>
                      {form.attends_remote && <span className="text-black text-xs font-bold">✓</span>}
                    </div>
                    <div>
                      <div className="flex items-center gap-2 text-sm font-medium">
                        <Video size={14} className={form.attends_remote ? "text-cyan-300" : "text-muted-foreground"}/> Atención Telemática
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-1">Videollamada. No requiere oficina.</div>
                    </div>
                  </div>
                </label>
              </div>
              {!form.attends_presencial && !form.attends_remote && (
                <div className="mt-2 text-[11px] text-amber-300 inline-flex items-center gap-1" data-testid="reg-modality-warning">
                  <AlertTriangle size={11}/> Debes marcar al menos una modalidad.
                </div>
              )}
            </div>

            {/* Dirección */}
            <Field label={form.attends_presencial ? "Dirección de tu oficina *" : "Dirección de tu oficina (opcional · sólo se revela tras pago inicial)"}>
              <input className="field" value={form.office_address} onChange={(e)=>set("office_address", e.target.value)} placeholder="Ej: Av. Providencia 1234, of. 501, Providencia" data-testid="reg-office-address" required={form.attends_presencial}/>
            </Field>

            {/* Tarifario */}
            <div>
              <div className="label-eyebrow">Tarifario neto de mediación</div>
              <p className="text-xs text-muted-foreground mt-1 mb-4">
                Ingresa dos valores netos que <strong>recibirás íntegros</strong>. Colega Link cobra por separado <strong>{CLP(initialFee)} fijo al iniciar</strong> y un <strong>{Math.round(agreementRate * 10000) / 100}% sólo sobre el monto adicional del acuerdo</strong>. Tope máximo total que paga el solicitante: <strong>{CLP(totalCap)}</strong>.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Precio por mediación frustrada (CLP)">
                  <input type="number" min={0} step={1000} className="field" placeholder="Ej: 50000"
                    value={form.precio_mediador_clp || ""}
                    onChange={(e)=>set("precio_mediador_clp", parseInt(e.target.value || "0", 10))}
                    data-testid="reg-precio-mediador"/>
                  <div className="text-[11px] text-cyan-300 mt-1.5" data-testid="reg-total-frustrada-hint">
                    Precio total al solicitante: <strong>{CLP(pricing.totalFrustrada)}</strong> (tu {CLP(pricing.base)} + {CLP(initialFee)} Colega Link)
                  </div>
                </Field>
                <Field label="Precio adicional con acuerdo total (CLP)">
                  <input type="number" min={0} step={1000} className="field" placeholder="Ej: 30000"
                    value={form.precio_acuerdo_clp || ""}
                    onChange={(e)=>set("precio_acuerdo_clp", parseInt(e.target.value || "0", 10))}
                    data-testid="reg-precio-acuerdo"/>
                  <div className="text-[11px] mt-1.5 flex items-start gap-1" data-testid="reg-max-adicional-hint">
                    <Info size={11} className="text-muted-foreground mt-0.5 shrink-0"/>
                    <span className="text-muted-foreground">El monto máximo por acuerdo es de <strong className="text-foreground">{CLP(pricing.maxAdicional)}</strong>, según el tope legal tras descontar tu honorario base y el cargo ColegaLink.</span>
                  </div>
                </Field>
              </div>

              {/* Calculadora de referencia visible */}
              <div className="mt-5 grid sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-md bg-white/5 border border-white/10 text-sm space-y-1" data-testid="reg-calc-frustrada">
                  <div className="label-eyebrow text-muted-foreground">Escenario · mediación frustrada</div>
                  <div className="flex justify-between mt-1"><span className="text-muted-foreground">Tu honorario</span><span className="font-medium">{CLP(pricing.base)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Cargo fijo Colega Link</span><span>{CLP(initialFee)}</span></div>
                  <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><span className="font-medium">Precio total al solicitante</span><span className="font-medium text-cyan-300">{CLP(pricing.totalFrustrada)}</span></div>
                  <div className="flex justify-between mt-2"><span className="text-emerald-300">Tú recibes</span><span className="text-emerald-300 font-medium">{CLP(pricing.base)}</span></div>
                </div>
                <div className={`p-4 rounded-md border text-sm space-y-1 ${pricing.overCap ? "border-[hsl(var(--destructive))]/40 bg-[hsl(var(--destructive))]/10" : "border-emerald-400/25 bg-emerald-400/5"}`} data-testid="reg-calc-acuerdo">
                  <div className="label-eyebrow text-emerald-300">Escenario · con acuerdo total</div>
                  <div className="flex justify-between mt-1"><span className="text-muted-foreground">Precio total frustrada (ya cobrado)</span><span>{CLP(pricing.totalFrustrada)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Precio adicional acuerdo</span><span>{CLP(pricing.adicional)}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">Comisión Colega Link ({Math.round(agreementRate * 10000) / 100}% del adicional)</span><span>{CLP(pricing.comision)}</span></div>
                  <div className="flex justify-between border-t border-white/10 pt-1 mt-1"><span className="font-medium">Total final al solicitante</span><span className={`font-medium ${pricing.overCap ? "text-[hsl(var(--destructive))]" : "text-emerald-300"}`}>{CLP(pricing.totalAcuerdo)}</span></div>
                  <div className="flex justify-between mt-2"><span className="text-emerald-300">Tú recibes total</span><span className="text-emerald-300 font-medium">{CLP(pricing.base + pricing.adicional)}</span></div>
                  {pricing.overCap && (
                    <div className="text-[11px] text-[hsl(var(--destructive))] mt-2 inline-flex items-start gap-1" data-testid="reg-over-cap-warning">
                      <AlertTriangle size={11} className="mt-0.5 shrink-0"/>
                      Excede el tope de {CLP(totalCap)}. Reduce el precio adicional (máx {CLP(pricing.maxAdicional)}).
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {form.role === "lawyer" && (
          <div className="mt-6 panel p-5" data-testid="reg-lawyer-photo-bank">
            <div className="label-eyebrow text-[hsl(var(--primary))]">Perfil y datos bancarios</div>
            <h3 className="serif text-2xl mt-1">Foto y cuenta para recibir pagos</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Los mandantes ven tu foto y decisión en el directorio. Tus datos bancarios son privados
              (no aparecen en tu perfil público) — sólo se usan para transferirte los honorarios de tus delegaciones.
            </p>

            {/* Foto de perfil */}
            <div className="mt-5">
              <div className="label-eyebrow text-xs">Foto de perfil (opcional)</div>
              <div className="mt-2 flex items-center gap-4">
                <div className="w-20 h-20 rounded-full border-2 border-white/10 bg-white/5 overflow-hidden flex items-center justify-center shrink-0" data-testid="reg-photo-preview">
                  {form.profile_photo_b64 ? (
                    <img src={`data:${form.profile_photo_mime};base64,${form.profile_photo_b64}`} alt="preview" className="w-full h-full object-cover"/>
                  ) : (
                    <span className="text-2xl text-muted-foreground">{(form.name || "?").charAt(0).toUpperCase()}</span>
                  )}
                </div>
                <div className="flex-1">
                  <label className="btn-ghost text-xs cursor-pointer inline-flex items-center gap-1.5">
                    <input type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const file = e.target.files?.[0]; if (!file) return;
                        if (file.size > 3 * 1024 * 1024) { setError("La foto excede 3 MB."); return; }
                        const b64 = await new Promise((ok, ko) => { const r = new FileReader(); r.onload = () => ok(String(r.result || "").split(",").pop()); r.onerror = ko; r.readAsDataURL(file); });
                        setForm((f) => ({ ...f, profile_photo_b64: b64, profile_photo_mime: file.type, profile_photo_name: file.name }));
                      }}
                      data-testid="reg-photo-input"/>
                    {form.profile_photo_b64 ? "Cambiar foto" : "Subir foto"}
                  </label>
                  {form.profile_photo_b64 && (
                    <button type="button" onClick={() => setForm((f) => ({ ...f, profile_photo_b64: "", profile_photo_mime: "", profile_photo_name: "" }))} className="ml-2 text-xs text-red-300 hover:text-red-200" data-testid="reg-photo-remove">Quitar</button>
                  )}
                  <p className="text-[11px] text-muted-foreground mt-1">JPG/PNG · máx 3 MB · aparece en el directorio público</p>
                </div>
              </div>
            </div>

            {/* Datos bancarios */}
            <div className="mt-6">
              <div className="label-eyebrow text-xs">Datos bancarios (opcional al registro)</div>
              <p className="text-[11px] text-muted-foreground mt-1 mb-3">Para recibir el honorario íntegro de cada delegación que aceptes.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Banco">
                  <input className="field" placeholder="Ej: Banco Estado" value={form.bank_name} onChange={(e) => set("bank_name", e.target.value)} data-testid="reg-bank-name"/>
                </Field>
                <Field label="Tipo de cuenta">
                  <select className="field" value={form.bank_account_type} onChange={(e) => set("bank_account_type", e.target.value)} data-testid="reg-bank-type">
                    <option value="">Selecciona…</option>
                    <option value="Cuenta Corriente">Cuenta Corriente</option>
                    <option value="Cuenta Vista">Cuenta Vista / CuentaRUT</option>
                    <option value="Cuenta Ahorro">Cuenta de Ahorro</option>
                  </select>
                </Field>
                <Field label="N° de cuenta">
                  <input className="field" placeholder="Sin puntos ni guiones" value={form.bank_account_number} onChange={(e) => set("bank_account_number", e.target.value.replace(/\s/g, ""))} data-testid="reg-bank-number"/>
                </Field>
                <Field label="RUT del titular">
                  <input className="field" placeholder="12345678-9" value={form.bank_holder_rut} onChange={(e) => set("bank_holder_rut", formatRut(e.target.value))} data-testid="reg-bank-rut"/>
                </Field>
                <Field label="Email para comprobantes" className="sm:col-span-2">
                  <input type="email" className="field" placeholder="tu@correo.cl" value={form.bank_email} onChange={(e) => set("bank_email", e.target.value)} data-testid="reg-bank-email"/>
                </Field>
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">
                Puedes completarlos después desde tu perfil, pero sin datos bancarios no podrás recibir honorarios.
              </p>
            </div>
          </div>
        )}

        {form.role === "lawyer" && (
          <div className="mt-6 panel p-5">
            <div className="label-eyebrow text-[hsl(var(--primary))]">Verificación de credencial</div>
            <h3 className="serif text-2xl mt-1">Certificado de título</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Sube el certificado emitido por la Corte Suprema (con firma electrónica). Lo usamos para validar
              tu calidad de abogado vía <a href="https://www.pjud.cl" target="_blank" rel="noreferrer" className="text-[hsl(var(--primary))] link-underline">pjud.cl</a>.
              Puedes subirlo ahora o después desde tu perfil.
            </p>
            <label className="drop-zone block mt-4" data-testid="reg-cert-zone">
              <input type="file" accept=".pdf" className="hidden" onChange={(e)=>onCertificateChange(e.target.files?.[0])} data-testid="reg-cert-input"/>
              <div className="serif text-lg">
                {form.certificate_filename || "Arrastra o haz clic para subir tu PDF"}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Solo PDF · máx 5MB · opcional</p>
            </label>
            {form.certificate_b64 && (
              <p className="text-xs text-[hsl(var(--accent))] mt-3" data-testid="reg-cert-uploaded">✓ Certificado adjunto · se enviará con tu registro</p>
            )}
          </div>
        )}

        {error && <div className="text-sm text-[hsl(var(--destructive))] mt-4" data-testid="reg-error">{error}</div>}

        <div className="mt-6 p-4 rounded-md border border-amber-400/25 bg-amber-400/[0.03]" data-testid="reg-terms-box">
          <label className="flex items-start gap-3 cursor-pointer text-sm">
            <input
              type="checkbox"
              className="mt-1 h-4 w-4 accent-amber-400 shrink-0"
              checked={form.terms_accepted}
              onChange={(e) => set("terms_accepted", e.target.checked)}
              data-testid="reg-terms-checkbox"
              required
            />
            <span className="text-muted-foreground leading-relaxed">
              Declaro haber leído y acepto los{" "}
              <Link to="/terminos" target="_blank" className="underline hover:text-foreground" data-testid="register-terms-link">
                Términos de Servicio y la cláusula de exención de responsabilidad
              </Link>{" "}
              de Colega Link. Autorizo el tratamiento de mis datos personales según la Ley 19.628.
              <span className="text-amber-300"> *</span>
            </span>
          </label>
        </div>

        <div className="mt-6 flex items-center gap-4">
          <button
            disabled={loading || !form.terms_accepted || (form.role === "mediator" && pricing.overCap)}
            className="btn-primary disabled:opacity-60"
            data-testid="reg-submit"
          >
            {loading ? "Creando…" : "Crear cuenta"}
          </button>
          <Link to="/login" className="text-sm link-underline">Ya tengo cuenta</Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children, className = "", hint = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="label-eyebrow">{label}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-red-300" data-testid="field-hint">{hint}</div>}
    </label>
  );
}

/** Ordinal en español para posiciones 1-99 (1er, 2°, 3°, …). */
function ordinal(n) {
  if (n === 1) return "1er";
  return `${n}°`;
}
